import { BasicLayout, RouteView } from '@/layouts'
import { Router } from './types'

// 修复：修正懒加载函数，直接使用完整路径，避免拼接错误
// 正确的懒加载写法：直接返回 import 函数，路径使用 @ 别名
const loadView = (viewPath: string) => {
  // 兼容不同路径格式（带/不带/、带.vue/不带.vue）
  const normalizedPath = viewPath.startsWith('/') 
    ? viewPath.slice(1) 
    : viewPath;
  const finalPath = normalizedPath.endsWith('.vue') 
    ? normalizedPath 
    : `${normalizedPath}.vue`;
  
  return () => import(/* webpackChunkName: "chunk-[request]" */ `@/views/${finalPath}`);
}

export const example: Router = {
  path: '/',
  name: 'index',
  component: BasicLayout,
  meta: { title: 'menu.home' },
  redirect: '/main',
  children: [
    // 仪表盘模块
    {
      path: '/dashboard',
      name: 'dashboard',
      redirect: '/dashboard/analysis',
      component: RouteView,
      meta: { 
        title: 'menu.dashboard.title', 
        icon: 'BankOutlined', 
        keepAlive: true, 
        permission: ['admin'] 
      },
      children: [
        // 修复：Home.vue 路径解析问题
        {
          path: '/main',
          name: 'Home',
          // 直接使用正确的路径格式
          component: () => import(/* webpackChunkName: "home" */ '@/views/Home.vue'),
          meta: { 
            title: 'menu.dashboard.home', 
            keepAlive: true, 
            permission: ['admin'] 
          }
        },
        {
          path: '/system/target_data',
          name: 'target_data',
          component: () => import(/* webpackChunkName: "target-data" */ '@/views/target/target_data/index.vue'),
          meta: { 
            title: 'menu.system.target_data', 
            keepAlive: true, 
            permission: ['admin'] 
          }
        },
        {
          path: '/system/target_cacl',
          name: 'target_cacl',
          component: () => import(/* webpackChunkName: "target-calc" */ '@/views/target/target_calc/index.vue'),
          meta: { 
            title: 'menu.system.target_calc', 
            keepAlive: true, 
            permission: ['admin'] 
          }
        }
      ]
    },

    // 基础数据模块
    {
      path: '/base',
      name: 'base',
      component: RouteView,
      redirect: '/base/dict_type',
      meta: { title: 'menu.base.title', icon: 'ProfileOutlined' },
      children: [
        {
          path: '/base/dict_type',
          name: 'dict_type',
          component: () => import(/* webpackChunkName: "dict-type" */ '@/views/base/dict_type/index.vue'),
          meta: { title: 'menu.base.dict_type', keepAlive: false, hiddenHeaderContent: true }
        },
        {
          path: '/base/dict_manage',
          name: 'dict_manage',
          component: () => import(/* webpackChunkName: "dict-manage" */ '@/views/base/dict_manage/index.vue'),
          meta: { title: 'menu.base.dict_manage', keepAlive: false, hiddenHeaderContent: true }
        },
        {
          path: '/base/dict_mapping',
          name: 'dict_mapping',
          component: () => import(/* webpackChunkName: "dict-manage" */ '@/views/base/dict_mapping/index.vue'),
          meta: { title: 'menu.base.dict_mapping', keepAlive: false, hiddenHeaderContent: true }
        },        
        {
          path: '/base/excel9',
          name: 'excel9',
          component: () => import(/* webpackChunkName: "dict-manage" */ '@/views/base/excel9/index.vue'),
          meta: { title: 'menu.base.excel9', keepAlive: false, hiddenHeaderContent: true }
        },        
      ]
    },

    // 系统管理模块
    {
      path: '/systemManage',
      name: 'systemManage',
      component: RouteView,
      redirect: '/system/module/index',
      meta: { title: 'menu.systemManage.title', icon: 'SettingOutlined' },
      children: [
        {
          path: '/system/menu_manage',
          name: 'menu_manage',
          component: () => import(/* webpackChunkName: "menu-manage" */ '@/views/system/menu_manage/index.vue'),
          meta: { title: 'menu.system.menu_manage', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/system/role_manage',
          name: 'role_manage',
          component: () => import(/* webpackChunkName: "role-manage" */ '@/views/system/role_manage/index.vue'),
          meta: { title: 'menu.system.role_manage', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/system/module/index',
          name: 'module',
          component: () => import(/* webpackChunkName: "module-manage" */ '@/views/system/module/index.vue'),
          meta: { title: 'menu.systemManage.module', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/system/report/list',
          name: 'report_list',
          component: () => import(/* webpackChunkName: "report-list" */ '@/views/system/report/list/index.vue'),
          meta: { title: 'menu.system.report_list', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/system/module_register',
          name: 'module_register',
          component: () => import(/* webpackChunkName: "module_register" */ '@/views/system/register/index.vue'),
          meta: { title: 'menu.system.register', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/system/report/show',
          name: 'report_show',
          component: () => import(/* webpackChunkName: "report-show" */ '@/views/system/report/show/index.vue'),
          props: (route) => ({
            repid: route.query.repid || '' // 修复：增加默认值，避免类型错误
          }),
          meta: { title: 'menu.system.report_show', keepAlive: true, permission: ['admin'], hidden: true }
        },
        {
          path: '/designer',
          name: 'Designer',
          component: () => import(/* webpackChunkName: "designer" */ '@/views/system/designer/index.vue'),
          meta: { title: 'menu.system.designer', keepAlive: true, permission: ['admin'] }
        },
        {
          path: '/dynamicForm',
          name: 'dynamicForm',
          component: () => import(/* webpackChunkName: "dynamic-form" */ '@/views/system/dynamicForm/index.vue'),
          meta: { title: 'menu.dynamicForm' }
        },
        {
          path: '/logicflow',
          name: 'logicflow',
          component: () => import(/* webpackChunkName: "logic-flow" */ '@/views/system/logicflow/index.vue'),
          meta: { title: 'menu.logicflow' }
        }
      ]
    },

    // 异常页面 - 修复i18n key问题
    {
      path: '/exception',
      name: 'exception',
      component: RouteView,
      redirect: '/exception/403',
      meta: { title: 'menu.exception.title', icon: 'UserOutlined', hidden: true },
      children: [
        {
          path: '/exception/403',
          name: '403',
          component: () => import(/* webpackChunkName: "exception-403" */ '@/views/system/exception/403.vue'),
          meta: { title: 'menu.exception.not-permission' } // 确保这个key在i18n中存在
        },
        {
          path: '/exception/404',
          name: '404',
          component: () => import(/* webpackChunkName: "exception-404" */ '@/views/system/exception/404.vue'),
          meta: { title: 'menu.exception.not-find' } // 确保这个key在i18n中存在
        },
        {
          path: '/exception/500',
          name: '500',
          component: () => import(/* webpackChunkName: "exception-500" */ '@/views/system/exception/500.vue'),
          meta: { title: 'menu.exception.server-error' } // 修复：替换缺失的i18n key
        }
      ]
    },

    {
      path: '/test',
      name: 'Test',
      component: () => import(/* webpackChunkName: "test-template" */ '@/views/template/index.vue'),
      meta: { title: 'menu.template.test', icon: 'BranchesOutlined', keepAlive: true, permission: ['admin'] }
    },

    {
      path: '/system/script',
      name: 'script',
      component: () => import(/* webpackChunkName: "sql-script" */ '@/views/system/script/index.vue'),
      meta: { title: 'menu.system.script', icon: 'BookOutlined', keepAlive: true, permission: ['admin'] }
    }
  ]
}